import { type ReactNode, useCallback, useState } from 'react'
import { safeOpen } from '@/shared/lib/safe-open'
import { Button, Drawer, Space, Spin } from 'antd'
import {
  CloseOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import {
  getFileExtension,
  isExcelExtension,
  isPdfExtension,
  isWordExtension,
} from './utils'
import type { DocumentViewerProps } from './types'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GOOGLE_VIEWER_BASE =
  'https://docs.google.com/viewer?embedded=true&url='

const OFFICE_VIEWER_BASE =
  'https://view.officeapps.live.com/op/embed.aspx?src='

const IFRAME_BASE_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  border: 'none',
  transition: 'opacity 0.3s',
}

const LOADING_OVERLAY_STYLE: React.CSSProperties = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 10,
}

const ERROR_CONTAINER_STYLE: React.CSSProperties = {
  textAlign: 'center' as const,
  padding: '50px',
  color: '#ff4d4f',
}

const DRAWER_BODY_STYLE: React.CSSProperties = {
  padding: 0,
  height: 'calc(100% - 60px)',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface DocumentMeta {
  title: string
  icon: ReactNode
  viewerUrl: string
}

function getDocumentMeta(
  ext: string,
  filePath: string
): DocumentMeta | null {
  const encodedPath = encodeURIComponent(filePath)

  if (isPdfExtension(ext)) {
    return {
      title: 'PDF Document',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      viewerUrl: `${GOOGLE_VIEWER_BASE}${encodedPath}`,
    }
  }

  if (isWordExtension(ext)) {
    return {
      title: 'Word Document',
      icon: <FileTextOutlined style={{ color: '#1890ff' }} />,
      viewerUrl: `${OFFICE_VIEWER_BASE}${encodedPath}`,
    }
  }

  if (isExcelExtension(ext)) {
    return {
      title: 'Excel Document',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      viewerUrl: `${OFFICE_VIEWER_BASE}${encodedPath}`,
    }
  }

  return null
}

function triggerDownload(href: string, filename: string): void {
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function DocumentViewer({
  file,
  onClose,
  visible,
}: DocumentViewerProps): ReactNode {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleLoad = useCallback(() => setLoading(false), [])

  const handleError = useCallback(() => {
    setLoading(false)
    setError('Failed to load document')
  }, [])

  const handleDownload = useCallback(() => {
    if (!file?.path) return
    triggerDownload(file.path, file.fileOriginalName ?? 'document')
  }, [file?.path, file?.fileOriginalName])

  const handleOpenExternal = useCallback(() => {
    if (file?.path) {
      safeOpen(file.path)
    }
  }, [file?.path])

  // Derive extension and meta
  const ext = file ? getFileExtension(file) : ''
  const filePath = file?.path ?? ''
  const meta = filePath ? getDocumentMeta(ext || file?.extension || '', filePath) : null

  // ---- Render helpers ----

  const renderIframe = (url: string, title: string) => (
    <div style={{ height: '100%', position: 'relative' as const }}>
      {loading && (
        <div style={LOADING_OVERLAY_STYLE}>
          <Spin size="large" />
        </div>
      )}
      <iframe
        src={url}
        style={{
          ...IFRAME_BASE_STYLE,
          opacity: loading ? 0 : 1,
        }}
        onLoad={handleLoad}
        onError={handleError}
        title={title}
      />
    </div>
  )

  const renderError = () => (
    <div style={ERROR_CONTAINER_STYLE}>
      <p>{error}</p>
      <Space>
        <Button onClick={handleDownload}>Download</Button>
        <Button type="primary" onClick={handleOpenExternal}>
          Open in New Tab
        </Button>
      </Space>
    </div>
  )

  const renderContent = () => {
    if (error) return renderError()
    if (!meta) return null
    return renderIframe(meta.viewerUrl, meta.title)
  }

  const drawerTitle = (
    <Space>
      {meta?.icon ?? <FileTextOutlined />}
      <span>{file?.fileOriginalName ?? meta?.title ?? 'Document'}</span>
    </Space>
  )

  return (
    <Drawer
      title={drawerTitle}
      size="80%"
      open={visible}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            type="text"
            title="Download"
          />
          <Button
            icon={<CloseOutlined />}
            onClick={onClose}
            type="text"
            title="Close"
          />
        </Space>
      }
      styles={{ body: DRAWER_BODY_STYLE }}
    >
      {renderContent()}
    </Drawer>
  )
}

export default DocumentViewer
